import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import type { IAddress } from "src/library/components/Address";
import useAxios from "src/core/shared/hooks/useAxios";
import type { IDrop } from "src/core/drops/api/drops.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";

export type CreateCustomerParams = Pick<ICustomer, "accountNumber" | "name" | "address" | "contactName" | "contactNumber" | "contactEmail">;
export type UpdateCustomerParams = Partial<ICustomer> & { id: number };
export type PartialCustomer = Partial<ICustomer>;
export type ListCustomersParams<T extends PartialCustomer = ICustomer> = { pagination: PaginatedParams<T>; filters: FilterParams<ICustomer & { accountManagerId?: number; }> };
export type ExportCustomerParams = { filters?: FilterParams<{ customerId: number }> };
export type DeactivateCustomerParams = { customerId: number; deleteOrders?: boolean };
export type ListUserCustomersParams = {userId: number, pagination: PaginatedParams<ICustomer>; filters: FilterParams<ICustomer>};

type CustomerEndpointConfig = {
  LIST_CUSTOMERS: EndpointConfig<
    ListCustomersParams,
    PaginatedData<ICustomer>
  >;
  EXPORT_DROPS: EndpointConfig<
    ExportCustomerParams,
    { message: string }
  >;
  GET_CUSTOMER: EndpointConfig<
    number,
    ICustomer
  >;
  CREATE_CUSTOMER: EndpointConfig<
    CreateCustomerParams,
    ICustomer
  >;
  UPDATE_CUSTOMER: EndpointConfig<
    UpdateCustomerParams,
    ICustomer
  >;
  UPDATE_CUSTOMER_STATUS: EndpointConfig<
    {
      customerId: number;
      status: ICustomer["status"];
      statusReason?: ICustomer["statusReason"];
      cancelOrders: boolean;
    },
    {message: string}
  >;
  LIST_USER_ASSIGNABLE_CUSTOMERS: EndpointConfig<
    ListUserCustomersParams,
    PaginatedData<ICustomer>
  >
  LIST_USER_CUSTOMERS: EndpointConfig<
    ListUserCustomersParams,
    PaginatedData<ICustomer>
  >
  GET_USER_CUSTOMER_COUNT: EndpointConfig<
    {userId: number},
    {count: number}
  >
};

const requests: RequestConfig<CustomerEndpointConfig> = {
  LIST_CUSTOMERS: params => {
    return {
      url: `${process.env.REACT_APP_API_URL}/core/customers`,
      method: "GET",
      params
    };
  },
  EXPORT_DROPS: params => {
    return {
      url: `${process.env.REACT_APP_API_URL}/core/customers/export/all`,
      method: "GET",
      params
    };
  },
  GET_CUSTOMER: id => ({
    url: `${process.env.REACT_APP_API_URL}/core/customers/${id}`,
    method: "GET"
  }),
  CREATE_CUSTOMER: data => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/customers`,
    method: "POST",
    data
  }),

  UPDATE_CUSTOMER: data => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/customers/${data.id}`,
    method: "PATCH",
    data
  }),

  UPDATE_CUSTOMER_STATUS: ({ customerId, ...data }) => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/customers/${customerId}/status`,
    method: "POST",
    data
  }),
  LIST_USER_ASSIGNABLE_CUSTOMERS: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/customers/assignable-user/${params.userId}`,
    method: "GET",
    params
  }),
  LIST_USER_CUSTOMERS: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/customers/user/${params.userId}`,
    method: "GET",
    params
  }),
  GET_USER_CUSTOMER_COUNT: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/customers/user/${params.userId}/count`,
    method: "GET",
    params
  })
};

export const useCustomerApi: UseApiRequest<CustomerEndpointConfig> = name => useAxios(requests[ name ], name);

export enum CustomerDropStatuses {
  Active = "active",
  Suspended = "suspended",
  OverCreditLimit = "over-credit-limit",
  Deactivated = "deactivated"
}
export interface ICustomer {
  id: number;
  accountNumber: string;
  name: string;
  address: IAddress;
  status: CustomerDropStatuses;
  statusReason?: string | null;
  branches: IDrop[];
  contactName: string;
  contactNumber: string;
  contactEmail: string;
  createdAt: string;
  updatedAt: string;
}
